import { Drawer, Modal, Spin } from "antd";
import { get, isEmpty, isNil, noop } from "lodash";
import moment from "moment";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { fieldPhrase } from "../../data-fields";
import { acceptedProposals, zoneField, zoneLabel } from "./zones";

const BASELINE_VERSION = 1;

const HistorySheet = ({
  open,
  template: { id },
  content,
  onClose,
  onRestore,
  onUndoProposal,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const HISTORY = t("HISTORY", { returnObjects: true });
  const [loadVersions, { data, loading, error }] = useLazyQuery(
    graphql.queries.DOCUMENT_TEMPLATE_VERSIONS,
    { fetchPolicy: "network-only", onError: noop },
  );

  useEffect(() => {
    if (open) loadVersions({ variables: { where: { id } } });
  }, [open, id, loadVersions]);

  const versions = get(data, "documentTemplateVersions") || [];
  const accepted = isNil(content) ? [] : acceptedProposals(content);

  const publishedText = (version) => {
    const date = moment(version.publishedAt).format(HISTORY.DATE_TIME_FORMAT);
    return isEmpty(version.publishedByName)
      ? t("HISTORY.PUBLISHED_ON", { date })
      : t("HISTORY.PUBLISHED_BY", { date, name: version.publishedByName });
  };

  const confirmRestore = (version) =>
    Modal.confirm({
      title: t("HISTORY.RESTORE_TITLE", { number: version.versionNumber }),
      content: HISTORY.RESTORE_CONTENT,
      okText: HISTORY.RESTORE_CONFIRM,
      cancelText: HISTORY.CANCEL,
      onOk: () => onRestore(version),
    });

  return (
    <Drawer
      open={open}
      width={440}
      title={HISTORY.TITLE}
      onClose={onClose}
      rootClassName="dynamic-documents-editor__sheet"
    >
      <h4 className="dynamic-documents-editor__step-title">
        {HISTORY.VERSIONS}
      </h4>
      {loading && <Spin size="small" />}
      {!loading && !isNil(error) && (
        <p className="dynamic-documents-editor__error">{HISTORY.LOAD_FAILED}</p>
      )}
      {!loading && isNil(error) && isEmpty(versions) && (
        <p className="dynamic-documents-editor__muted">{HISTORY.NO_VERSION}</p>
      )}
      <ul className="dynamic-documents-editor__history">
        {versions.map((version) => (
          <li
            key={version.id}
            className="dynamic-documents-editor__history-row"
          >
            <div>
              <strong>
                {t("HISTORY.VERSION", { number: version.versionNumber })}
              </strong>
              <div className="dynamic-documents-editor__muted">
                {isNil(version.publishedByName) &&
                version.versionNumber === BASELINE_VERSION
                  ? HISTORY.BASELINE
                  : publishedText(version)}
              </div>
            </div>
            <Button
              type="primary--outlined"
              size="small"
              onClick={() => confirmRestore(version)}
            >
              {HISTORY.RESTORE}
            </Button>
          </li>
        ))}
      </ul>
      <h4 className="dynamic-documents-editor__step-title">{HISTORY.LUCIE}</h4>
      {isEmpty(accepted) ? (
        <p className="dynamic-documents-editor__muted">{HISTORY.NO_LUCIE}</p>
      ) : (
        <ul className="dynamic-documents-editor__history">
          {accepted.map(({ zone }) => (
            <li key={zone.id} className="dynamic-documents-editor__history-row">
              <div>
                <strong>{zoneLabel(zone, t)}</strong>
                <div className="dynamic-documents-editor__muted">
                  {fieldPhrase(zoneField(zone, content.anchorMappings))}
                </div>
              </div>
              <button
                type="button"
                className="dynamic-documents-editor__link"
                onClick={() => onUndoProposal(zone)}
              >
                {HISTORY.UNDO}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
};

export default HistorySheet;
